/**
 * discoverVideoController — BUG #927e (Gordon 2026-08-21).
 *
 * A single feed-level controller that guarantees the Discover video-portrait
 * cards behave calmly instead of "too much" autoplay. It enforces, for the
 * whole feed at once:
 *
 *   1. loop = false            — each card's <video> never loops (enforced in
 *                                the component; the controller never re-plays a
 *                                clip that has ended).
 *   2. play ONCE then stop     — when a clip fires `ended` the controller
 *                                releases it and does NOT auto-replay it; a
 *                                per-card cooldown keeps it paused for a while.
 *   3. ONE active video at a time — `requestActive(id)` pauses whatever else is
 *                                playing before it plays the requested card, so
 *                                two cards can never play simultaneously.
 *   4. hover selects the active — a hover calls `requestActive(id, {hover:true})`
 *                                which always wins, stopping any other playing
 *                                card immediately.
 *   5. a 5000ms initial page gate — NO autoplay (ambient) is permitted until 5s
 *                                after the first card registers (i.e. the feed
 *                                mounts). Hover is a user action, not autoplay,
 *                                so it is allowed to open the gate early.
 *
 * This supersedes #927a's per-card ~1.4s defer with a page-level 5s gate plus a
 * strict one-at-a-time governor.
 *
 * Pure and framework-free so it can be unit-tested directly (aY). The default
 * export is a shared singleton used by every VideoPortraitPreview on the page;
 * `createDiscoverVideoController` builds an isolated instance for tests.
 */

export const INITIAL_AUTOPLAY_DELAY_MS = 5000;
// After a clip ends (or is stopped following an ambient burst) it may not become
// the ambient-active clip again for this long — "plays once then stays paused
// for a while, do not immediately replay".
export const REPLAY_COOLDOWN_MS = 12000;

export function createDiscoverVideoController(options = {}) {
  const now = options.now || (() => Date.now());
  // Wrap in arrows so the global timer functions resolve at CALL time — this is
  // what lets jest's fake timers (which swap the globals) drive the singleton.
  const setTimeoutFn = options.setTimeoutFn || ((fn, ms) => setTimeout(fn, ms));
  const clearTimeoutFn = options.clearTimeoutFn || ((t) => clearTimeout(t));
  const initialDelay =
    typeof options.initialDelayMs === "number"
      ? options.initialDelayMs
      : INITIAL_AUTOPLAY_DELAY_MS;
  const cooldownMs =
    typeof options.cooldownMs === "number"
      ? options.cooldownMs
      : REPLAY_COOLDOWN_MS;

  // id -> { play, stop }
  const registry = new Map();
  // id -> timestamp when this card last ended/was released (cooldown anchor)
  const lastEndedAt = new Map();
  // ids that currently WANT to ambient-autoplay (on-screen, has a clip, motion
  // allowed). The scheduler promotes at most one of them to active at a time.
  const wanting = new Set();

  let activeId = null;
  let gateOpen = false;
  let gateTimer = null;

  function eligibleAmbient(id) {
    if (!registry.has(id)) return false;
    const endedAt = lastEndedAt.get(id);
    if (endedAt != null && now() - endedAt < cooldownMs) return false;
    return true;
  }

  // CALM scheduler: if the gate is open and NOTHING is playing, promote a single
  // wanting card to active. Deliberately does NOT auto-pick a replacement when a
  // clip ends — the freed slot stays idle until a NEW event (a card scrolls in,
  // a hover) so a grid never rotates like a wall of motion. One-at-a-time.
  function schedule() {
    if (!gateOpen || activeId !== null) return;
    for (const id of wanting) {
      if (eligibleAmbient(id)) {
        requestActive(id);
        return;
      }
    }
  }

  const openGate = () => {
    gateOpen = true;
    gateTimer = null;
    schedule();
  };

  const armGate = () => {
    if (gateOpen || gateTimer) return;
    gateTimer = setTimeoutFn(openGate, initialDelay);
  };

  function register(id, handlers) {
    registry.set(id, handlers);
    // Anchor the 5s page gate to the moment the feed first mounts a card.
    armGate();
    return () => unregister(id);
  }

  function unregister(id) {
    registry.delete(id);
    lastEndedAt.delete(id);
    wanting.delete(id);
    if (activeId === id) activeId = null;
  }

  /**
   * Mark whether a card wants to ambient-autoplay (on-screen + has clip). The
   * scheduler decides IF/when it actually plays (5s gate, one-at-a-time,
   * cooldown). Turning wanting OFF pauses it if it was the active card.
   */
  function setWanting(id, want) {
    if (want) {
      wanting.add(id);
      schedule();
    } else {
      wanting.delete(id);
      if (activeId === id) release(id);
    }
  }

  function isGateOpen() {
    return gateOpen;
  }

  function getActiveId() {
    return activeId;
  }

  // INVARIANT: never more than one active video.
  function activeCount() {
    return activeId === null ? 0 : 1;
  }

  function stopActiveExcept(id) {
    if (activeId !== null && activeId !== id) {
      const prev = registry.get(activeId);
      if (prev && typeof prev.stop === "function") prev.stop();
    }
  }

  /**
   * Ask to become the single active (playing) card.
   * @param {string} id
   * @param {{hover?: boolean}} opts hover requests bypass the initial autoplay
   *        gate and the replay cooldown (they are user-initiated).
   * @returns {boolean} whether this card is now the active, playing one.
   */
  function requestActive(id, opts = {}) {
    const hover = !!opts.hover;

    if (!hover) {
      // Ambient autoplay is gated for the first 5s of the page...
      if (!gateOpen) return false;
      // ...and a just-ended card must cool down before it may autoplay again.
      const endedAt = lastEndedAt.get(id);
      if (endedAt != null && now() - endedAt < cooldownMs) return false;
    }

    // One-at-a-time: stop whoever else is playing FIRST.
    stopActiveExcept(id);
    activeId = id;

    const cur = registry.get(id);
    if (cur && typeof cur.play === "function") {
      const started = cur.play();
      if (started === false) {
        // Could not actually start (e.g. detached node) — don't hold the slot.
        if (activeId === id) activeId = null;
        return false;
      }
    }
    return true;
  }

  // Card finished its single play-through (video 'ended', loop=false).
  function notifyEnded(id) {
    lastEndedAt.set(id, now());
    if (activeId === id) activeId = null;
  }

  // Card is no longer wanting to play (hover leave / unmount).
  function release(id) {
    if (activeId === id) {
      activeId = null;
      const cur = registry.get(id);
      if (cur && typeof cur.stop === "function") cur.stop();
    }
  }

  function destroy() {
    if (gateTimer) {
      clearTimeoutFn(gateTimer);
      gateTimer = null;
    }
    registry.clear();
    lastEndedAt.clear();
    wanting.clear();
    activeId = null;
    gateOpen = false;
  }

  // Full reset of runtime state — the shared singleton persists across a test
  // file, so tests call this in beforeEach to isolate gate/cooldown/active state.
  function reset() {
    destroy();
  }

  return {
    register,
    unregister,
    setWanting,
    requestActive,
    notifyEnded,
    release,
    isGateOpen,
    getActiveId,
    activeCount,
    destroy,
    reset,
    // test seam
    _openGateNow: openGate,
    INITIAL_AUTOPLAY_DELAY_MS: initialDelay,
    REPLAY_COOLDOWN_MS: cooldownMs,
  };
}

// Shared feed-level singleton — every Discover card on the page coordinates
// through this one instance, which is what makes "only one plays at a time"
// and the single 5s gate true across the whole feed.
const discoverVideoController = createDiscoverVideoController();

export default discoverVideoController;
